/* eslint-disable jsx-a11y/control-has-associated-label */
import { AppIcon } from '@/components/ui/icons';
import { Button } from '@/features/ui/components/Button';
import { IconButton } from '@/components/ui/IconButton';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import type { OrganizationAccountRole } from '../../../types';
import BrandLogo from '../../../components/BrandLogo';
import StandaloneSurface from '../../../components/Layout/StandaloneSurface';
import { getPasswordFeedback, validatePassword } from '../../../utils/passwordUtils';

interface SignUpFormProps {
  onToggleMode: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onToggleMode }) => {
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Self-registration always starts at the least-privileged canonical
    // account role. Authority is granted later via invitation/admin action.
    role: 'organization_member' as OrganizationAccountRole,
    department: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setError(getPasswordFeedback(passwordValidation));
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await signUp(formData.email, formData.password, {
        name: formData.name,
        role: formData.role,
        department: formData.department
      });

      if (error) {
        setError(error);
      } else {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          role: 'organization_member', // Use valid account role, not scoped relationship
          department: ''
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StandaloneSurface measure="reading">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">

        {/* Logo & Header */}
        <div className="text-center mb-2xl">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mb-lg flex items-center justify-center"
          >
            <BrandLogo variant="auth" />
          </motion.div>
          <p className="text-body mt-2">Create your account</p>
        </div>

        {/* Success Message */}
        {success && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-xl card-padding-sm bg-status-success-soft border border-status-success/30 rounded-lg">
            <div className="flex items-center space-x-sm text-status-success-foreground">
              <AppIcon name="checkCircle" className="w-4 h-4" />
              <span className="text-sm font-medium">Account created successfully!</span>
            </div>
            <p className="text-sm text-status-success-foreground mt-1">
              Check your email for verification instructions.
            </p>
          </motion.div>
        )}

        {/* Sign Up Form */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card card-padding-lg">
          <form onSubmit={handleSubmit} className="space-xl">

            {/* Name Field */}
            <div>
              <label className="block text-caption mb-sm">Full Name</label>
              <div className="relative">
                <AppIcon name="user" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted w-5 h-5" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 border border-border-subtle rounded-lg focus-ring transition-all"
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-caption mb-sm">Email Address</label>
              <div className="relative">
                <AppIcon name="mail" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted w-5 h-5" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full pl-10 pr-4 py-3 border border-border-subtle rounded-lg focus-ring transition-all"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Roles are assigned by the licence owner or workspace invitation. */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-caption mb-sm">Department (Optional)</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  className="w-full px-4 py-3 border border-border-subtle rounded-lg focus-ring transition-all"
                  placeholder="e.g., Engineering, Marketing"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-caption mb-sm">Password</label>
              <div className="relative">
                <AppIcon name="lock" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full pl-10 pr-12 py-3 border border-border-subtle rounded-lg focus-ring transition-all"
                  placeholder="Create a password"
                  required
                />
                <IconButton
                  label={showPassword ? 'Hide password' : 'Show password'}
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  icon={showPassword ? <AppIcon name="eyeOff" className="h-4 w-4" /> : <AppIcon name="eye" className="h-4 w-4" />}
                />
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-caption mb-sm">Confirm Password</label>
              <div className="relative">
                <AppIcon name="lock" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted w-5 h-5" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full pl-10 pr-12 py-3 border border-border-subtle rounded-lg focus-ring transition-all"
                  placeholder="Confirm your password"
                  required
                />
                <IconButton
                  label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                  size="sm"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  icon={showConfirmPassword ? <AppIcon name="eyeOff" className="h-4 w-4" /> : <AppIcon name="eye" className="h-4 w-4" />}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="p-3 bg-status-danger-soft border border-status-danger/30 rounded-lg">
                <div className="flex items-center space-x-sm text-status-danger-foreground">
                  <AppIcon name="alertCircle" className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <Button type="button" variant="link" size="sm" onClick={onToggleMode} className="h-auto px-0">
              Already have an account? Sign in
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </StandaloneSurface>
  );
};

export default SignUpForm;
